import { all, call, cancel, fork, put, take, takeEvery } from 'redux-saga/effects';
import {
  connectCompanyDriveResult,
  loadUserByIdSuccess,
  setCredentialModerateCountSocket,
  setEventModerateCountSocket,
  setUsersOnline,
  updateRoleAssignmentSuccess,
} from 'actions/entities/userActions';
import {
  deleteEntity,
  fetchResource,
  getIntlMessage,
  loadEntities,
  LOGOUT_ACTION_TYPES,
  showErrorMessage,
  showSuccessMessage,
  updateEntity,
} from 'helpers/utilHelpers';
import {
  CHANGE_USER_PASSWORD,
  CONNECT_COMPANY_DRIVE,
  DELETE_DEPARTMENT,
  DELETE_FREELANCER,
  DELETE_POSITION,
  DELETE_USER,
  DELETE_USER_ROLE,
  DISMISS_USER,
  LOAD_CLIENT_PROFILES,
  LOAD_CLIENT_USER_BY_ID,
  LOAD_CMS_LIST,
  LOAD_COMPANIES,
  LOAD_COMPANY_BY_ID,
  LOAD_DEPARTMENTS,
  LOAD_DISMISS_USER,
  LOAD_FREELANCERS,
  LOAD_MODERATE_COUNT,
  LOAD_POSITIONS,
  LOAD_STRUCTURE_CHART,
  LOAD_USER_BY_ID,
  LOAD_USER_LAST_TASK,
  LOAD_USER_ROLES,
  LOAD_USERS,
  PASSWORD_FILLING,
  SUBSCRIBE_ONLINE,
  UNSUBSCRIBE_ONLINE,
  UPDATE_CLIENT_USER,
  UPDATE_DEPARTMENT,
  UPDATE_FREELANCER,
  UPDATE_POSITION,
  UPDATE_ROLE_ASSIGNMENT,
  UPDATE_USER,
  UPDATE_USER_COMPANY,
  UPDATE_USER_ROLE,
  UPDATE_USERNAME,
  DISCONNECT_TELEGRAM,
  RESEND_CONFIRM_COMPANY,
  SUBSCRIBE_CREDENTIAL_MODERATE_COUNT,
  UNSUBSCRIBE_CREDENTIAL_MODERATE_COUNT,
  SUBSCRIBE_EVENT_MODERATE_COUNT,
  UNSUBSCRIBE_EVENT_MODERATE_COUNT,
  DELETE_CLIENT_PROFILE,
} from 'constants/ActionTypes';
import { toggleUserRoleForm } from 'actions/ui/formActions';
import SocketService from 'services/SocketService';
import { SOCKET } from 'constants/SocketConstants';
import { hideLoader } from 'actions/settingActions';

import { USER_PROFILES } from 'actions/entities/userActions';

const socketInstance = SocketService.Instance;

function* fetchUserById(action) {
  try {
    const response = yield call(() => fetchResource(USER_PROFILES + '/' + action.userId));
    yield put(loadUserByIdSuccess(response));
  } catch {
    yield showErrorMessage(getIntlMessage('user.loadById.error'));
  }
}

function* fetchRoleAssignmentUpdate(action) {
  const { resolve, reject } = action.promise;
  let resource = 'assignments';
  let method = 'POST';
  if (action.values.userId) {
    resource += '/' + action.values.userId;
    method = 'PUT';
  }
  try {
    const response = yield call(() =>
      fetchResource(
        resource,
        method,
        {
          item_name: action.values.roleName,
          userId: action.values.userId,
        },
        {},
        {},
        'rbac',
      ),
    );

    yield put(updateRoleAssignmentSuccess(response));

    resolve();

    yield put(toggleUserRoleForm());
    yield showSuccessMessage(getIntlMessage('request.success'));
  } catch (response) {
    const data = yield response.json();
    if (response.status && response.status === 422) {
      reject(data);
      return;
    }
    yield showErrorMessage(getIntlMessage('request.error'));
  }
}

function* loadUserById() {
  yield takeEvery(LOAD_USER_BY_ID, fetchUserById);
}

function* loadUserLastTask() {
  yield takeEvery(LOAD_USER_LAST_TASK, loadEntities);
}

function* fetchCompanyDriveConnect(action) {
  const { resource, data } = action.payload;

  try {
    const response = yield call(() => fetchResource(resource, 'POST', null, null, data));
    yield put(
      connectCompanyDriveResult({
        companyId: data?.companyId,
        status: 204,
        data: response,
      }),
    );
  } catch (response) {
    if (response) {
      const data = yield response.json();

      if (response.status) {
        yield put(
          connectCompanyDriveResult({
            status: response.status,
            data,
          }),
        );
      }
    }
  }

  yield put(hideLoader());
}

// Load sagas
// ==========
function* loadDepartments() {
  yield takeEvery(LOAD_DEPARTMENTS, loadEntities);
}

function* loadUsers() {
  yield takeEvery(LOAD_USERS, loadEntities);
}

function* loadPositions() {
  yield takeEvery(LOAD_POSITIONS, loadEntities);
}

function* loadUserRoles() {
  yield takeEvery(LOAD_USER_ROLES, loadEntities);
}

function* loadFreelancers() {
  yield takeEvery(LOAD_FREELANCERS, loadEntities);
}

function* loadStructureChart() {
  yield takeEvery(LOAD_STRUCTURE_CHART, loadEntities);
}

function* loadModerateCount() {
  yield takeEvery(LOAD_MODERATE_COUNT, loadEntities);
}

// Update/delete sagas
// ===================
function* updateUserRole() {
  yield takeEvery(UPDATE_USER_ROLE, updateEntity);
}

function* changeUserPassword() {
  yield takeEvery(CHANGE_USER_PASSWORD, updateEntity);
}

function* deleteUserRole() {
  yield takeEvery(DELETE_USER_ROLE, deleteEntity);
}

function* updateRoleAssignment() {
  yield takeEvery(UPDATE_ROLE_ASSIGNMENT, fetchRoleAssignmentUpdate);
}

function* updateDepartment() {
  yield takeEvery(UPDATE_DEPARTMENT, updateEntity);
}

function* deleteDepartment() {
  yield takeEvery(DELETE_DEPARTMENT, deleteEntity);
}

function* updatePosition() {
  yield takeEvery(UPDATE_POSITION, updateEntity);
}

function* deletePosition() {
  yield takeEvery(DELETE_POSITION, deleteEntity);
}

function* updateFreelancer() {
  yield takeEvery(UPDATE_FREELANCER, updateEntity);
}

function* deleteFreelancer() {
  yield takeEvery(DELETE_FREELANCER, deleteEntity);
}

function* dismissUser() {
  yield takeEvery(DISMISS_USER, updateEntity);
}

function* deleteClientProfile() {
  yield takeEvery(DELETE_CLIENT_PROFILE, deleteEntity);
}

function* loadDismissUser() {
  yield takeEvery(LOAD_DISMISS_USER, loadEntities);
}

function* updateUser() {
  yield takeEvery(UPDATE_USER, updateEntity);
}

function* changeUsername() {
  yield takeEvery(UPDATE_USERNAME, updateEntity);
}

function* loadCmsList() {
  yield takeEvery(LOAD_CMS_LIST, loadEntities);
}

function* loadCompanies() {
  yield takeEvery(LOAD_COMPANIES, loadEntities);
}

function* loadCompanyById() {
  yield takeEvery(LOAD_COMPANY_BY_ID, loadEntities);
}

function* updateClientUser() {
  yield takeEvery(UPDATE_CLIENT_USER, updateEntity);
}

function* loadClientProfiles() {
  yield takeEvery(LOAD_CLIENT_PROFILES, loadEntities);
}

function* loadClientUserById() {
  yield takeEvery(LOAD_CLIENT_USER_BY_ID, loadEntities);
}

function* deleteUser() {
  yield takeEvery(DELETE_USER, deleteEntity);
}

function* updateUserCompany() {
  yield takeEvery(UPDATE_USER_COMPANY, updateEntity);
}

function* connectCompanyDrive() {
  yield takeEvery(CONNECT_COMPANY_DRIVE, fetchCompanyDriveConnect);
}

function* passwordFilling() {
  yield takeEvery(PASSWORD_FILLING, updateEntity);
}

function* subscribeOnline() {
  while (true) {
    yield take(SUBSCRIBE_ONLINE);

    const channel = yield call(socketInstance.onSubscribe, SOCKET.online);
    const task = yield takeEvery(channel, function*(data) {
      yield put(setUsersOnline(data));
    });

    yield take([...LOGOUT_ACTION_TYPES, UNSUBSCRIBE_ONLINE]);

    channel.close();
    yield cancel(task);

    yield call(socketInstance.onUnsubscribe, SOCKET.online);
  }
}

function* subscribeCredentialModerateCount() {
  while (true) {
    yield take(SUBSCRIBE_CREDENTIAL_MODERATE_COUNT);

    const socketChannel = yield call(socketInstance.onSubscribe, SOCKET.credentialModerateCount);
    const task = yield takeEvery(socketChannel, function*(data) {
      yield put(setCredentialModerateCountSocket(data));
    });

    yield take([...LOGOUT_ACTION_TYPES, UNSUBSCRIBE_CREDENTIAL_MODERATE_COUNT]);

    socketChannel.close();
    yield cancel(task);

    yield call(socketInstance.onUnsubscribe, SOCKET.credentialModerateCount);
  }
}

function* subscribeEventModerateCount() {
  while (true) {
    yield take(SUBSCRIBE_EVENT_MODERATE_COUNT);

    const socketChannel = yield call(socketInstance.onSubscribe, SOCKET.eventModerateCount);
    const task = yield takeEvery(socketChannel, function*(data) {
      yield put(setEventModerateCountSocket(data));
    });

    yield take([...LOGOUT_ACTION_TYPES, UNSUBSCRIBE_EVENT_MODERATE_COUNT]);

    socketChannel.close();
    yield cancel(task);

    yield call(socketInstance.onUnsubscribe, SOCKET.eventModerateCount);
  }
}

function* disconnectTelegram() {
  yield takeEvery(DISCONNECT_TELEGRAM, loadEntities);
}

function* reSendConfirmEmail() {
  yield takeEvery(RESEND_CONFIRM_COMPANY, updateEntity);
}

export default function* rootSaga() {
  yield all([
    fork(loadCmsList),
    fork(loadDismissUser),
    fork(dismissUser),
    fork(updateUser),
    fork(loadDepartments),
    fork(loadUsers),
    fork(loadPositions),
    fork(loadUserById),
    fork(loadUserRoles),
    fork(updateUserRole),
    fork(deleteUserRole),
    fork(updateRoleAssignment),
    fork(updateDepartment),
    fork(deleteDepartment),
    fork(updatePosition),
    fork(deletePosition),
    fork(loadFreelancers),
    fork(updateFreelancer),
    fork(deleteFreelancer),
    fork(loadCompanies),
    fork(loadCompanyById),
    fork(updateClientUser),
    fork(loadClientProfiles),
    fork(loadClientUserById),
    fork(deleteUser),
    fork(deleteClientProfile),
    fork(loadStructureChart),
    fork(subscribeOnline),
    fork(subscribeCredentialModerateCount),
    fork(subscribeEventModerateCount),
    fork(loadUserLastTask),
    fork(loadModerateCount),
    fork(updateUserCompany),
    fork(connectCompanyDrive),
    fork(passwordFilling),
    fork(changeUsername),
    fork(disconnectTelegram),
    fork(reSendConfirmEmail),
    // Loaders
    fork(changeUserPassword),
  ]);
}
